import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Header } from '@/components'
import { Button, Card, Tabs, TabsList, TabsTrigger } from '@/components/ui'
import { AxiosError } from 'axios'
import { Flashlight, SwitchCamera } from 'lucide-react'
import { scanCheckIn, scanCheckOut } from '../../../core/services/scan-tickets'
import { QrScanner } from './QrScanner'
import {
  ScanValidationModal,
  type TicketHolder,
  type ValidationStatus,
} from './ScanValidationModal'
import { Icon } from '@/common/components/Icon'

type ScanTab = 'check-in' | 'check-out'

export const ScanTicketsPage: React.FC = () => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<ScanTab>('check-in')
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>(
    'environment',
  )
  const [paused, setPaused] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('valid')
  const [message, setMessage] = useState('')
  const [ticketHolder, setTicketHolder] = useState<TicketHolder | null>(null)

  const handleScanSuccess = useCallback(
    async (decodedText: string) => {
      if (paused) return
      setPaused(true)

      try {
        const scanFn = activeTab === 'check-in' ? scanCheckIn : scanCheckOut
        const { data } = await scanFn(decodedText)

        setValidationStatus('valid')
        setMessage('')
        setTicketHolder({
          name: data.name,
          ticketType: data.ticketType,
          email: data.email,
          phone: data.phone,
        })
        setShowModal(true)
      } catch (e) {
        const axiosError = e as AxiosError<{
          message?: string
          ticketHolder?: TicketHolder
        }>
        const status = axiosError.response?.status
        const resData = axiosError.response?.data

        if (status === 409) {
          setValidationStatus('already-scanned')
          setMessage(
            resData?.message ||
              'Please Note! This QR Code were already scanned.',
          )
          setTicketHolder(resData?.ticketHolder ?? null)
        } else {
          setValidationStatus('not-valid')
          setMessage(resData?.message || 'Warning, this QR Code is not valid.')
          setTicketHolder(null)
        }
        setShowModal(true)
      } finally {
        setTimeout(() => setPaused(false), 2000)
      }
    },
    [activeTab, paused],
  )

  const handleScanError = useCallback((error: string) => {
    setValidationStatus('not-valid')
    setMessage(error)
    setTicketHolder(null)
    setShowModal(true)
  }, [])

  const handleCancel = () => {
    navigate(-1)
  }

  const toggleCamera = () => {
    setCameraFacing((prev) => (prev === 'environment' ? 'user' : 'environment'))
  }

  return (
    <>
      <Header pageTitle="Scan Tickets" />

      <Card className="p-0 !bg-monochrome-4-60 relative overflow-hidden h-[calc(100vh-120px)] flex flex-col">
        <div className="flex justify-center py-4 absolute top-0 left-0 w-full z-10">
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as ScanTab)}
          >
            <TabsList>
              <div className="bg-monochrome-4-60 p-2.5 rounded-[20px]">
                <TabsTrigger value="check-in">Check-In</TabsTrigger>
                <TabsTrigger value="check-out">Check-Out</TabsTrigger>
              </div>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 relative overflow-hidden min-h-0">
          <QrScanner
            onScanSuccess={handleScanSuccess}
            onScanError={handleScanError}
            cameraFacing={cameraFacing}
            paused={paused}
          />
        </div>

        <div className="flex items-center justify-center gap-4 py-6 absolute bottom-0 left-0 w-full z-10">
          <Button
            variant="secondary"
            className="w-[90px] h-[44px] rounded-full p-0 bg-monochrome-120"
            onClick={() => {}}
          >
            <Icon icon="flash" width="18" height="18" viewBox="0 0 18 18" />
          </Button>
          <Button
            variant="secondary"
            className="w-[90px] h-[44px] rounded-full p-0 bg-monochrome-120"
            onClick={toggleCamera}
          >
            <Icon icon="camera" width="18" height="18" viewBox="0 0 18 18" />
          </Button>
          <Button
            variant="secondary"
            className="w-[90px] h-[44px] rounded-full px-6 bg-monochrome-120"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </Card>

      <ScanValidationModal
        open={showModal}
        onOpenChange={setShowModal}
        status={validationStatus}
        message={message}
        ticketHolder={ticketHolder}
      />
    </>
  )
}
